import React, { useRef, useState } from "react"
import styled from "styled-components"
import { SUCCESS_STORIES_DATA } from "./helpers"
import { AosWrapper } from "../../molecules/AosWrapper"
import { TimelineComponent } from "../../molecules/TimelineComponent"
import { SuccessTimelineComponent } from "../../molecules/SuccessTimelineComponent"
import {
  clamp,
  scrollWindowToNestedItem,
  useNestedScroll,
} from "../../hooks/useNestedScroll"

const Wrapper = styled.div`
  background: ${(props) => props.theme.bgSectorColor};
  width: 100%;
  scroll-margin-top: 96px;
  position: relative;
  min-height: 400vh;

  @media only screen and (min-width: 769px) {
    height: 400vh;
    min-height: unset;
  }
`

const StickyViewport = styled.div`
  @media only screen and (max-width: 768px) {
    position: sticky;
    top: 0px;
    height: 100vh;
    overflow: hidden;
    z-index: 4;
    display: flex;
    flex-direction: column;
    min-height: 0;

    & > * {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      width: 100%;
    }
  }

  @media only screen and (min-width: 769px) {
    position: sticky;
    top: 106px;
    height: calc(100vh - 106px);
    overflow: hidden;
    z-index: 4;
  }
`

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 90px 100px 90px 150px;
  box-sizing: border-box;

  @media only screen and (min-width: 769px) {
    height: calc(100vh - 106px);
  }

  @media only screen and (max-width: 1100px) {
    padding: 100px 40px 100px 40px;
  }

  @media only screen and (max-width: 768px) {
    padding: 0px 20px 0px 20px;
    align-items: start;
    height: 100vh;
  }
`

const ScrollableDiv = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  scroll-snap-type: y mandatory;
  scroll-behavior: auto;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  ::-webkit-scrollbar {
    width: 0; /* Remove scrollbar space */
    height: 0;
  }

  @media only screen and (max-width: 768px) {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
  }
`

const MainTitle = styled.p`
  ${(props) => props?.theme?.typography?.sectionHeadline2};
  line-height: 33px;
  font-weight: 600;
  font-size: 1.375rem;
  color: ${(props) => props.theme.base};
  span {
    font-size: 1.375rem;
    background: linear-gradient(87.75deg, #0058ee 100%, #36d7b7 80%);
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    @media only screen and (max-width: 768px) {
      font-size: 1.375rem;
      line-height: 33px;
    }
  }

  @media only screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    padding: 40px 20px 20px 20px;
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 33px;
  }

  @media only screen and (min-width: 769px) {
    display: none;
  }
`

const SuccessStories = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRef = useRef<HTMLDivElement>(null)
  const scrollableRef = useRef<HTMLDivElement>(null)
  const containerChildRef = useRef(null)

  useNestedScroll(sectionRef, scrollableRef, {
    stickyTop: 106,
    mobileStickyTop: 96,
  })

  const handleScroll = () => {
    const container = scrollableRef.current
    if (!container) return

    // Use the center of the viewport to determine active index
    const center = container.scrollTop + container.clientHeight / 2
    const itemHeight = container.scrollHeight / SUCCESS_STORIES_DATA.length
    const visibleIndex = Math.floor(center / itemHeight)

    setActiveIndex(clamp(visibleIndex, 0, SUCCESS_STORIES_DATA.length - 1))
  }

  const scrollToItem = (index: number) => {
    const listItem = document.getElementsByClassName("list-item")?.[index] as
      | HTMLElement
      | undefined
    listItem?.classList.remove("hidden")

    const section = sectionRef.current
    const container = scrollableRef.current
    if (!listItem || !section || !container) return

    scrollWindowToNestedItem(section, container, listItem, {
      stickyTop: 106,
      mobileStickyTop: 96,
    })
  }

  return (
    <Wrapper id={"success-stories"} ref={sectionRef}>
      <StickyViewport>
        <AosWrapper>
          <MainTitle>
            {"Success stories in "} <br />
            <span>{"Ready to Work"}</span>
          </MainTitle>
          <Container>
            <TimelineComponent
              onSideLineClick={scrollToItem}
              timelineData={SUCCESS_STORIES_DATA}
              activeIndex={activeIndex}
            />
            <ScrollableDiv
              ref={scrollableRef}
              id={"parent"}
              onScroll={handleScroll}
            >
              {SUCCESS_STORIES_DATA?.map((success_story, i) => {
                return (
                  <SuccessTimelineComponent
                    className={"list-item"}
                    refChild={containerChildRef}
                    key={i}
                    name={success_story?.name}
                    position={success_story?.position.toUpperCase()}
                    story={success_story?.story}
                    index={i}
                    imgSrc={success_story?.imgSrc}
                  />
                )
              })}
            </ScrollableDiv>
          </Container>
        </AosWrapper>
      </StickyViewport>
    </Wrapper>
  )
}

export { SuccessStories }
